import XhrRequestHandler from '../../lib/XhrRequestHandler';
import { Categoria, CategoriaJSON } from '../Categoria/Categoria';
import { Torneo, TorneoJSON } from '../Torneo/Torneo';
import { Division, DivisionJSON } from '../Division/Division';
import { Equipo, EquipoJSON } from '../Equipo/Equipo';
import { JugadorXEquipo, JugadorXEquipoJSON } from '../JugadorXEquipo/JugadorXEquipo';
import { Temporada, TemporadaJSON } from '../Temporada/Temporada';
import { Nacionalidad, Genero, Jugador, JugadorJSON } from './Jugador';
import { JugadorConEquipo, JugadorConEquipoJSON } from './JugadorConEquipo';

export interface ImageSavedPath {
  savedPath: string;
}

export interface HabilitacionFilters {
  jug_apellido: string;
  jug_documento_numero: string;
  cat_nombre: string;
  jug_genero: string;
  club_nombre: string;
  jug_apto_medico: string;
}

export interface JugadorFilters {
  jug_apellido: string;
  jug_documento_numero: string;
  jug_genero: string;
  jug_estado: string;
  cat_nombre: string;
  jug_apto_medico: string;
  jug_activo: string;
  club_nombre: string;
  [index: string]: any;
}

export class JugadorSystem {
  private readonly base = '/jugadores';
  constructor(private requestHandler: XhrRequestHandler) {}

  async getJugadorIdentifiedBy(id: string): Promise<Jugador> {
    const data = await this.requestHandler.get<JugadorJSON>(`${this.base}/${id}`);
    return Jugador.fromJSON(data);
  }

  /**
   *
   * @param id
   * @returns CUIDADO: devuelve jugador sin importar la federacion del usuario logoneado
   */
  async getJugadorById(id: string): Promise<Jugador> {
    const data = await this.requestHandler.get<JugadorJSON>(`${this.base}/by-id/${id}`);
    return Jugador.fromJSON(data);
  }

  async actualizarImagen(jugId: string, path: string) {
    await this.requestHandler.put<string, unknown>(`${this.base}/actualizar-imagen/${jugId}`, {
      image: path,
    });
  }

  async getDivision(jugId: string): Promise<Division | null> {
    const data = await this.requestHandler.get<DivisionJSON | null>(`${this.base}/division?jug_id=${jugId}`);
    return data ? Division.fromJSON(data) : null;
  }

  async getJugadoresIdNoLibres(temActualId: string): Promise<string[]> {
    const data = await this.requestHandler.get<string[]>(
      `${this.base}/jugadores-no-libres?tem_id=${temActualId}`
    );
    return data;
  }

  async getJugadorXNroDocumento(nroDocumento: string, tipoDocumento: string): Promise<Jugador | null> {
    const data = await this.requestHandler.get<JugadorJSON | null>(
      `${this.base}/nro-documento?nro_documento=${nroDocumento}&dt_id=${tipoDocumento}`
    );
    return data ? Jugador.fromJSON(data) : null;
  }

  async getUltimoEquipoInfo(jugId: string): Promise<{
    Division: Division;
    Categoria: Categoria;
    Temporada: Temporada;
    Equipo: Equipo;
    Torneo: Torneo;
  } | null> {
    const data = await this.requestHandler.get<{
      Division: DivisionJSON;
      Categoria: CategoriaJSON;
      Temporada: TemporadaJSON;
      Equipo: EquipoJSON;
      Torneo: TorneoJSON;
    } | null>(`${this.base}/equipo-anterior?jug_id=${jugId}`);
    const ret = data
      ? {
          Division: Division.fromJSON(data.Division),
          Equipo: Equipo.fromJSON(data.Equipo),
          Categoria: Categoria.fromJSON(data.Categoria),
          Temporada: Temporada.fromJSON(data.Temporada),
          Torneo: Torneo.fromJSON(data.Torneo),
        }
      : null;
    return ret;
  }

  async getList(): Promise<Jugador[]> {
    const data = await this.requestHandler.get<JugadorJSON[]>(`${this.base}`);
    return data.map((entity) => Jugador.fromJSON(entity));
  }

  async getListForDropdown(): Promise<Jugador[]> {
    const data = await this.requestHandler.get<JugadorJSON[]>(`${this.base}/for-dropdown`);
    return data.map((entity) => Jugador.fromJSON(entity));
  }

  async save(entity: Jugador) {
    const data = entity.toJSON();
    const savedEntity = await this.requestHandler.post<JugadorJSON, JugadorJSON>(this.base, data);
    return Jugador.fromJSON(savedEntity);
  }

  async getGeneroList(): Promise<Genero[]> {
    const data = await this.requestHandler.get<Genero[]>(`${this.base}/generos`);
    return data;
  }

  //   async getNacionalidadList(): Promise<Nacionalidad[]> {
  //     const data = await this.requestHandler.get<Nacionalidad[]>(`${this.base}/nacionalidades`);
  //     return data;
  //   }

  async getJugadoresDisponibles(equipoId: string, temporadaId: string): Promise<Jugador[]> {
    //, temporadaId: string
    const params = '?eq_id=' + equipoId + '&tem_id=' + temporadaId; // + "&tem_id=" + temporadaId
    const data = await this.requestHandler.get<JugadorJSON[]>(`${this.base}/jugadores-disponibles${params}`);
    return data.map((entity) => Jugador.fromJSON(entity));
  }

  async getOtrosJugadores(equipoId: string): Promise<JugadorConEquipo[]> {
    const params = '?eq_id=' + equipoId;
    const data = await this.requestHandler.get<JugadorConEquipoJSON[]>(
      `${this.base}/jugadores-otros-equipos${params}`
    );
    return data.map((entity) => JugadorConEquipo.createFrom(Jugador.fromJSON(entity), entity.eq_nombre));
  }

  getBaseUrlForDatagrid() {
    return this.requestHandler.requestUrl(`${this.base}/datagrid`);
  }

  getUrlForImage(image: string) {
    return this.requestHandler.requestUrl(`/uploads/images/jugador/${image}`);
  }

  async getJugadoresPendHabilitacion(): Promise<Jugador[]> {
    const data = await this.requestHandler.get<JugadorJSON[]>(`${this.base}/pend-habilitacion`);
    return data.map((entity) => Jugador.fromJSON(entity));
  }

  getUrlForJugadoresPendHabilitacion(fedId: string) {
    return this.requestHandler.requestUrl(`${this.base}/pend-habilitacion/${fedId}`);
  }

  getUrlForJugadoresPendPermisoHabilitacion() {
    return this.requestHandler.requestUrl(`${this.base}/pend-permiso-habilitacion`);
  }

  getUrlForJugadoresPendPrimerPermisoHabilitacion() {
    return this.requestHandler.requestUrl(`${this.base}/pend-primer-permiso-habilitacion`);
  }

  async getJugadoresPendRenovHabilitacion(): Promise<Jugador[]> {
    const data = await this.requestHandler.get<JugadorJSON[]>(`${this.base}/pend-renov-habilitacion`);
    return data.map((entity) => Jugador.fromJSON(entity));
  }

  getUrlForJugadoresPendRenovHabilitacion(fedId: string) {
    return this.requestHandler.requestUrl(`${this.base}/pend-renov-habilitacion/${fedId}`);
  }

  async rechazarHabilitacion(id: string, fedId: string): Promise<unknown> {
    return await this.requestHandler.put(`${this.base}/rechazar-habilitacion/${id}/${fedId}`, {});
  }

  async rechazarPrimeraHabilitacion(id: string, fedId: string): Promise<unknown> {
    return await this.requestHandler.put(`${this.base}/rechazar-primera-habilitacion/${id}/${fedId}`, {});
  }

  async aceptarPrimeraHabilitacion(id: string, fedId: string): Promise<unknown> {
    return await this.requestHandler.put(`${this.base}/aceptar-primera-habilitacion/${id}/${fedId}`, {});
  }

  async aceptarRenovacionHabilitacion(id: string, fedId: string): Promise<unknown> {
    return await this.requestHandler.put(`${this.base}/aceptar-renovacion-habilitacion/${id}/${fedId}`, {});
  }

  async aceptarPrimerasHabilitaciones(
    filters: HabilitacionFilters,
    fedId: string
  ): Promise<{ ok: number; error: number }> {
    return await this.requestHandler.put(`${this.base}/aceptar-primeras-habilitaciones/${fedId}`, filters);
  }

  async aceptarRenovacionesHabilitaciones(
    filters: HabilitacionFilters,
    fedId: string
  ): Promise<{ ok: number; error: number }> {
    return await this.requestHandler.put(
      `${this.base}/aceptar-renovaciones-habilitaciones/${fedId}`,
      filters
    );
  }

  async rechazarHabilitaciones(
    filters: HabilitacionFilters,
    fedId: string
  ): Promise<{ ok: number; error: number }> {
    return await this.requestHandler.put(`${this.base}/rechazar-habilitaciones/${fedId}`, filters);
  }

  async rechazarPrimerasHabilitaciones(
    filters: HabilitacionFilters,
    fedId: string
  ): Promise<{ ok: number; error: number }> {
    return await this.requestHandler.put(`${this.base}/rechazar-primeras-habilitaciones/${fedId}`, filters);
  }

  async habilitarPendientesDeHabilitacion(): Promise<{ message: string }> {
    return await this.requestHandler.put(`${this.base}/habilitar-pendientes-habilitacion`, {});
  }

  async habilitarPendientesDeRenovacionHabilitacion(): Promise<{ message: string }> {
    return await this.requestHandler.put(`${this.base}/habilitar-pendientes-renovacion-habilitacion`, {});
  }

  async permitirHabilitacion(id: string): Promise<unknown> {
    return await this.requestHandler.put(`${this.base}/permitir-habilitacion/${id}`, {});
  }

  async permitirPrimeraHabilitacion(id: string): Promise<unknown> {
    return await this.requestHandler.put(`${this.base}/permitir-primera-habilitacion/${id}`, {});
  }

  async permitirHabilitaciones(filters: HabilitacionFilters): Promise<{ ok: number; error: number }> {
    return await this.requestHandler.put(`${this.base}/permitir-habilitaciones`, filters);
  }

  async permitirPrimerasHabilitaciones(filters: HabilitacionFilters): Promise<{ ok: number; error: number }> {
    return await this.requestHandler.put(`${this.base}/permitir-primeras-habilitaciones`, filters);
  }

  async permitirHabilitacionesParaTodos(): Promise<{ message: string }> {
    return await this.requestHandler.put(`${this.base}/permitir-habilitaciones-todos`, {});
  }

  async rechazarPermisoHabilitacion(id: string): Promise<unknown> {
    return await this.requestHandler.put(`${this.base}/rechazar-permiso-habilitacion/${id}`, {});
  }

  async rechazarPermisoPrimeraHabilitacion(id: string): Promise<unknown> {
    return await this.requestHandler.put(`${this.base}/rechazar-permiso-primera-habilitacion/${id}`, {});
  }

  async rechazarPermisosHabilitaciones(filters: HabilitacionFilters): Promise<{ ok: number; error: number }> {
    return await this.requestHandler.put(`${this.base}/rechazar-permisos-habilitaciones`, filters);
  }

  async rechazarPermisosPrimerasHabilitaciones(
    filters: HabilitacionFilters
  ): Promise<{ ok: number; error: number }> {
    return await this.requestHandler.put(`${this.base}/rechazar-permisos-primeras-habilitaciones`, filters);
  }

  async getEstadosDisponibles(): Promise<string[]> {
    const data = await this.requestHandler.get<string[]>(`${this.base}/estados-disponibles`);
    return data;
  }

  async getJugadoresByClubList(clubIdList: string[]): Promise<Jugador[]> {
    const url = `${this.base}/jugadores-by-club-list`;
    const data = await this.requestHandler.post<JugadorJSON[]>(url, { clubes: clubIdList });
    return data.map((entity) => Jugador.fromJSON(entity));
  }

  async imprimirCarnets(filters: JugadorFilters) {
    const attrs = Object.keys(filters);
    let params = '';
    attrs.forEach((attr) => {
      if (filters[attr]) {
        if (params !== '') {
          params += '&';
        }
        params += attr + '=' + filters[attr];
      }
    });
    await this.requestHandler.open(`${this.base}/imprimir-carnets${params ? '?' + params : ''}`);
  }
}
